<template>
  <div class="template-center" :class="{ 'is-embedded': embedded }">
    <aside class="template-sidebar">
      <div class="template-sidebar-head">
        <div>
          <h4>模板列表</h4>
          <p>点击模板或「通用导出」筛选记录（再点取消），点「导出」预览并下载</p>
        </div>
        <el-button text size="small" :loading="templatesLoading" @click="loadTemplates">刷新</el-button>
      </div>
      <el-input
        v-model="templateKeyword"
        clearable
        size="small"
        placeholder="搜索模板 / 项目"
        class="template-search"
      />
      <el-tag v-if="isAdmin" type="warning" effect="plain" size="small" class="template-admin-tag">
        管理员：可见并修改全部模板
      </el-tag>
      <div class="template-list" v-loading="templatesLoading">
        <article
          class="template-item template-item--generic"
          :class="{ 'is-active': recordGenericOnly }"
          @click="toggleGenericExportFilter"
        >
          <div class="template-item-head">
            <strong>通用导出</strong>
            <el-tag size="small" type="info" effect="plain">无模板</el-tag>
          </div>
          <p class="template-item-meta">看板直接导出、未保存模板</p>
          <p class="template-item-meta">{{ projectCode ? '仅当前项目' : '全部项目' }}</p>
        </article>
        <el-empty v-if="!templatesLoading && !filteredTemplates.length" description="暂无模板" :image-size="56" />
        <article
          v-for="tpl in filteredTemplates"
          :key="tpl.id"
          class="template-item"
          :class="{ 'is-active': selectedTemplateId === tpl.id }"
          @click="selectTemplateForRecords(tpl)"
        >
          <div class="template-item-head">
            <strong :title="tpl.alias">{{ tpl.alias }}</strong>
            <el-tag size="small" :type="tpl.visibility === 1 ? 'success' : 'info'" effect="plain">
              {{ tpl.visibility === 1 ? '公开' : '个人' }}
            </el-tag>
          </div>
          <p class="template-item-meta">code:{{ tpl.project_code || '-' }}</p>
          <p class="template-item-meta">创建人：{{ tpl.created_name || tpl.created_by }}</p>
          <div
            class="template-item-actions"
            :class="{ 'is-single': !canEditTemplateItem(tpl) }"
            @click.stop
          >
            <button
              type="button"
              class="tpl-act tpl-act--primary"
              :class="{ 'is-loading': loadingPreviewId === tpl.id }"
              :disabled="loadingPreviewId === tpl.id"
              @click="openTemplateExport(tpl)"
            >{{ loadingPreviewId === tpl.id ? '导出…' : '导出' }}</button>
            <button
              v-if="canEditTemplateItem(tpl)"
              type="button"
              class="tpl-act tpl-act--primary"
              @click="openTemplateEdit(tpl)"
            >修改</button>
            <button
              v-if="canEditTemplateItem(tpl)"
              type="button"
              class="tpl-act tpl-act--danger"
              @click="handleDeleteTemplate(tpl)"
            >删除</button>
          </div>
        </article>
      </div>
    </aside>

    <section class="record-main">
      <div class="record-main-head">
        <div>
          <h4>导出记录</h4>
          <p v-if="recordGenericOnly">
            当前筛选：通用导出（无模板）<template v-if="projectCode"> · 当前项目</template>
          </p>
          <p v-else-if="recordTemplateFilter">
            当前筛选模板：{{ recordTemplateFilter.alias }}
            <template v-if="recordProjectCode && !projectCode"> · code:{{ recordProjectCode }}</template>
          </p>
          <p v-else>历史导出任务，支持下载与失败重跑</p>
        </div>
        <el-tag v-if="isAdmin" type="warning" effect="plain" size="small">管理员视图</el-tag>
      </div>

      <div class="record-toolbar">
        <el-input
          v-if="!projectCode"
          v-model="recordProjectCode"
          clearable
          placeholder="code 筛选"
          style="width: 120px"
          @keyup.enter="searchRecords"
          @clear="searchRecords"
        />
        <el-input
          v-model="keyword"
          clearable
          placeholder="关键词（项目名/ code / 模板）"
          style="width: 200px"
          @keyup.enter="searchRecords"
          @clear="searchRecords"
        />
        <el-select v-model="statusFilter" clearable placeholder="全部状态" style="width: 120px" @change="searchRecords">
          <el-option label="等待中" value="pending" />
          <el-option label="进行中" value="running" />
          <el-option label="已完成" value="done" />
          <el-option label="失败" value="failed" />
        </el-select>
        <el-button type="primary" plain :loading="loading" @click="searchRecords">查询</el-button>
        <el-button
          v-if="recordGenericOnly || recordTemplateFilter || recordProjectCode || keyword || statusFilter"
          plain
          @click="clearRecordFilters"
        >清除</el-button>
      </div>

      <div class="record-table-wrap">
        <el-table
          :data="records"
          stripe
          v-loading="loading"
          height="100%"
          :empty-text="recordTableEmptyText"
        >
          <el-table-column prop="id" label="ID" width="72" />
          <el-table-column v-if="!projectCode" label="项目" min-width="140" show-overflow-tooltip>
            <template #default="{ row }">
              <span class="record-project-code-label">code:{{ row.project_code || '-' }}</span>
              <span v-if="row.project_name" class="record-project-name">{{ row.project_name }}</span>
            </template>
          </el-table-column>
          <el-table-column label="日期范围" min-width="210" show-overflow-tooltip>
            <template #default="{ row }">{{ row.date_start }} ~ {{ row.date_end }}</template>
          </el-table-column>
          <el-table-column label="模板" min-width="140" show-overflow-tooltip>
            <template #default="{ row }">{{ row.template_alias || '通用导出' }}</template>
          </el-table-column>
          <el-table-column label="状态" width="92">
            <template #default="{ row }">
              <el-tag :type="statusTagType(row.status)" size="small">{{ statusLabel(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="result_size" label="行数" width="72">
            <template #default="{ row }">{{ row.result_size > 0 ? row.result_size : '-' }}</template>
          </el-table-column>
          <el-table-column label="操作人" min-width="100" show-overflow-tooltip>
            <template #default="{ row }">{{ row.created_name || row.created_by }}</template>
          </el-table-column>
          <el-table-column label="创建时间" min-width="150">
            <template #default="{ row }">{{ row.created_at }}</template>
          </el-table-column>
          <el-table-column label="操作" width="132" fixed="right" class-name="record-actions-col">
            <template #default="{ row }">
              <div class="record-action-buttons">
                <button
                  v-if="row.status === 'done' && row.result_size > 0"
                  type="button"
                  class="rec-act rec-act--primary"
                  :class="{ 'is-loading': downloadingId === row.id }"
                  :disabled="downloadingId === row.id"
                  @click="handleDownload(row)"
                >{{ downloadingId === row.id ? '下载…' : '下载' }}</button>
                <button
                  v-if="row.status === 'failed'"
                  type="button"
                  class="rec-act rec-act--warn"
                  :class="{ 'is-loading': rerunningId === row.id }"
                  :disabled="rerunningId === row.id"
                  @click="handleRerun(row)"
                >{{ rerunningId === row.id ? '重跑…' : '重跑' }}</button>
                <el-popover
                  v-if="row.status === 'failed' && row.error_msg"
                  placement="left" width="320" trigger="click"
                >
                  <template #reference>
                    <button type="button" class="rec-act rec-act--danger">原因</button>
                  </template>
                  <pre class="error-pre">{{ row.error_msg }}</pre>
                </el-popover>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="record-pagination">
        <el-pagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          background
          layout="total, prev, pager, next"
          :total="total"
          @current-change="load"
        />
      </div>

      <div class="export-progress-panel" v-if="exportProgress.visible">
        <div class="export-progress-msg" :title="exportProgress.message">{{ exportProgress.message }}</div>
        <el-progress :percentage="exportProgress.pct" :stroke-width="12" :status="exportProgress.status" />
        <div class="export-progress-current" v-if="exportProgress.currentLabel" :title="exportProgress.currentLabel">
          正在处理：<strong>{{ exportProgress.currentLabel }}</strong>
        </div>
      </div>
    </section>

    <ExportExcelPreviewDialog
      v-model="previewDialogVisible"
      :title="`预览并导出：${previewTemplate?.alias || ''}`"
      :config="previewConfig"
      :loading="!!loadingPreviewId"
      :hint="previewMetaHint"
      :exporting="exporting"
      show-export
      @export="handleExportFromPreview"
    />

    <ExportTemplateMetaDialog
      v-model="metaDialogVisible"
      :template="editingTemplate"
      @saved="handleTemplateMetaSaved"
    />

    <el-dialog v-model="downloadProgressVisible" title="正在准备 Excel" width="380px"
      :close-on-click-modal="false" :show-close="false" align-center append-to-body>
      <div class="dl-progress-panel">
        <div class="dl-progress-msg">{{ downloadProgressMsg }}</div>
        <el-progress :percentage="downloadProgressPct" :stroke-width="16" :status="downloadProgressStatus" />
      </div>
      <template #footer>
        <el-button
          v-if="downloadProgressStatus === 'success' || downloadProgressStatus === 'exception'"
          @click="downloadProgressVisible = false"
        >关闭</el-button>
      </template>
    </el-dialog>

    <el-dialog v-model="rerunProgressVisible" title="重新执行导出" width="520px"
      :close-on-click-modal="false" :show-close="false" align-center append-to-body>
      <div class="dl-progress-panel">
        <div class="dl-progress-msg" :title="rerunProgressMsg">{{ rerunProgressMsg }}</div>
        <el-progress :percentage="rerunProgressPct" :stroke-width="16" :status="rerunProgressStatus" />
        <div class="dl-progress-current" v-if="rerunProgress.currentLabel" :title="rerunProgress.currentLabel">
          正在处理：<strong>{{ rerunProgress.currentLabel }}</strong>
        </div>
      </div>
      <template #footer>
        <el-button
          v-if="rerunProgressStatus === 'success' || rerunProgressStatus === 'exception'"
          @click="rerunProgressVisible = false"
        >关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  fetchExportRecords, fetchRecordWithData, markRecordRunning, markRecordDone, markRecordFailed,
  createExportRecord, fetchTemplates, fetchTemplateById, deleteTemplate, canEditTemplate, isExportAdmin
} from '../reportDataTemplateApi'
import { exportWorkbook, buildExportFileName, getThemeConfig } from '../reportDataExport'
import { runExportFromConfig } from '../reportDataTemplateExport'
import { createExportProgressController } from '../exportProgressUtil'
import ExportExcelPreviewDialog from './ExportExcelPreviewDialog.vue'
import ExportTemplateMetaDialog from './ExportTemplateMetaDialog.vue'
import { resolveProjectIdByCode } from '../reportDataApi'

const props = defineProps({
  projectCode: { type: String, default: null },
  project:     { type: Object, default: null },
  adminUsers:  { type: Array,  default: () => [] },
  projects:    { type: Array,  default: () => [] },
  embedded:    { type: Boolean, default: false }
})

const emit = defineEmits(['export-started'])

const loading = ref(false)
const records = ref([])
const keyword = ref('')
const recordProjectCode = ref('')
const recordTemplateFilter = ref(null)
const recordGenericOnly = ref(false)
const statusFilter = ref('')
const page = ref(1)
const pageSize = ref(50)
const total = ref(0)
const downloadingId = ref(null)
const rerunningId = ref(null)
const exporting = ref(false)

const templatesLoading = ref(false)
const templates = ref([])
const templateKeyword = ref('')
const selectedTemplateId = ref(null)
const loadingPreviewId = ref(null)
const previewDialogVisible = ref(false)
const previewTemplate = ref(null)
const previewConfig = ref(null)
const metaDialogVisible = ref(false)
const editingTemplate = ref(null)

const exportProgress = reactive({
  visible: false,
  message: '',
  pct: 0,
  status: '',
  currentLabel: ''
})
let exportProgressCtrl = null

const downloadProgressVisible = ref(false)
const downloadProgressMsg = ref('')
const downloadProgressPct = ref(0)
const downloadProgressStatus = ref('')

const rerunProgressVisible = ref(false)
const rerunProgress = reactive({
  message: '', pct: 0, status: '', currentLabel: ''
})
const rerunProgressMsg = computed(() => rerunProgress.message)
const rerunProgressPct = computed(() => rerunProgress.pct)
const rerunProgressStatus = computed(() => rerunProgress.status)
let rerunProgressCtrl = null

const isAdmin = computed(() => isExportAdmin(props.adminUsers))

const filteredTemplates = computed(() => {
  let list = templates.value
  if (templateKeyword.value) {
    const kw = templateKeyword.value.toLowerCase()
    list = list.filter((tpl) =>
      (tpl.alias || '').toLowerCase().includes(kw) ||
      (tpl.project_name || '').toLowerCase().includes(kw) ||
      (tpl.project_code || '').toLowerCase().includes(kw) ||
      (tpl.created_name || '').toLowerCase().includes(kw) ||
      (tpl.created_by || '').toLowerCase().includes(kw)
    )
  }
  return list
})

const recordTableEmptyText = computed(() => {
  if (recordGenericOnly.value) {
    return props.projectCode ? '当前项目暂无通用导出记录' : '暂无通用导出记录'
  }
  if (recordTemplateFilter.value) {
    return `模板「${recordTemplateFilter.value.alias}」暂无导出记录`
  }
  return '暂无导出记录'
})

const previewMetaHint = computed(() => {
  if (!previewConfig.value) return ''
  const f = previewConfig.value.exportForm || {}
  const orient = f.orientation === 'date-row' ? '日期做表头' : '日期做列头'
  const summary = f.includeSummaryRow
    ? `含汇总行（${[f.summaryDedup ? '去重' : '', f.summaryCumDaily ? '累加' : ''].filter(Boolean).join('+') || '求和'}）`
    : '无汇总行'
  return `${orient} · ${summary} · 预览为示意结构，确认日期后点「开始导出」`
})


function selectTemplateForRecords(tpl) {
  if (selectedTemplateId.value === tpl.id) {
    clearRecordFilters()
    return
  }
  recordGenericOnly.value = false
  selectedTemplateId.value = tpl.id
  recordTemplateFilter.value = { id: tpl.id, alias: tpl.alias || '' }
  keyword.value = ''
  if (!props.projectCode && tpl.project_code) {
    recordProjectCode.value = tpl.project_code
  }
  searchRecords()
}

function toggleGenericExportFilter() {
  if (recordGenericOnly.value) {
    clearRecordFilters()
    return
  }
  recordGenericOnly.value = true
  recordTemplateFilter.value = null
  selectedTemplateId.value = null
  keyword.value = ''
  searchRecords()
}

function clearRecordFilters() {
  recordTemplateFilter.value = null
  recordGenericOnly.value = false
  selectedTemplateId.value = null
  keyword.value = ''
  recordProjectCode.value = ''
  statusFilter.value = ''
  searchRecords()
}

function searchRecords() {
  page.value = 1
  load()
}

function canEditTemplateItem(tpl) {
  return canEditTemplate(tpl, props.adminUsers)
}

async function refresh() {
  await Promise.all([load(), loadTemplates()])
}

onMounted(refresh)

async function loadTemplates() {
  templatesLoading.value = true
  try {
    const rows = await fetchTemplates(props.projectCode, { adminUsers: props.adminUsers })
    templates.value = Array.isArray(rows) ? rows : []
  } catch (err) {
    templates.value = []
    ElMessage.error('加载模板失败：' + (err.message || ''))
  } finally {
    templatesLoading.value = false
  }
}

async function load() {
  loading.value = true
  try {
    const rows = await fetchExportRecords({
      projectCode: props.projectCode || null,
      filterProjectCode: !props.projectCode && recordProjectCode.value.trim()
        ? recordProjectCode.value.trim()
        : null,
      templateId: recordGenericOnly.value ? null : (recordTemplateFilter.value?.id ?? null),
      templateAlias: recordGenericOnly.value ? null : (recordTemplateFilter.value?.alias || null),
      genericExportOnly: recordGenericOnly.value,
      keyword: keyword.value.trim() || null,
      status: statusFilter.value || null,
      adminUsers: props.adminUsers,
      page: page.value,
      size: pageSize.value
    })
    records.value = Array.isArray(rows) ? rows : []
    total.value = records.value.length
  } catch (err) {
    ElMessage.error('加载记录失败：' + (err.message || ''))
  } finally {
    loading.value = false
  }
}

async function openTemplateExport(tpl) {
  previewDialogVisible.value = true
  previewTemplate.value = tpl
  previewConfig.value = null
  loadingPreviewId.value = tpl.id
  try {
    const detail = await fetchTemplateById(tpl.id)
    if (!detail) throw new Error('模板不存在')
    previewConfig.value = typeof detail.config_json === 'string'
      ? JSON.parse(detail.config_json)
      : detail.config_json
  } catch (err) {
    ElMessage.error('加载模板失败：' + err.message)
    previewDialogVisible.value = false
  } finally {
    loadingPreviewId.value = null
  }
}

function openTemplateEdit(tpl) {
  if (!canEditTemplateItem(tpl)) {
    ElMessage.warning('仅创建者或管理员可修改此模板')
    return
  }
  editingTemplate.value = { ...tpl }
  metaDialogVisible.value = true
}

function handleTemplateMetaSaved(payload) {
  const idx = templates.value.findIndex((item) => item.id === payload.id)
  if (idx >= 0) {
    templates.value.splice(idx, 1, {
      ...templates.value[idx],
      alias: payload.alias,
      visibility: payload.visibility
    })
  }
}

async function handleDeleteTemplate(tpl) {
  if (!canEditTemplateItem(tpl)) return
  try {
    await ElMessageBox.confirm(`确定删除模板「${tpl.alias}」吗？`, '删除模板', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await deleteTemplate(tpl.id)
    templates.value = templates.value.filter((item) => item.id !== tpl.id)
    if (selectedTemplateId.value === tpl.id) {
      clearRecordFilters()
    }
    ElMessage.success('模板已删除')
  } catch (action) {
    if (action !== 'cancel' && action !== 'close') {
      ElMessage.error('删除失败：' + (action?.message || action || ''))
    }
  }
}

async function resolveProjectForExport(tpl) {
  if (props.project?.id && (!props.projectCode || props.project.code === tpl?.project_code)) {
    return props.project
  }
  const code = tpl?.project_code
  if (!code) throw new Error('模板缺少项目信息')
  const cached = props.projects.find((p) => p.code === code)
  if (cached?.id) return cached
  const id = await resolveProjectIdByCode(code)
  if (!id) throw new Error(`无法解析项目（${code}）`)
  return { id, code, name: tpl.project_name || code, version: cached?.version || 'v4' }
}

async function handleExportFromPreview({ config, dateRange }) {
  if (!config || !dateRange?.[0] || !previewTemplate.value) return
  previewDialogVisible.value = false
  await runExportJob(config, {
    templateId: previewTemplate.value.id,
    templateAlias: previewTemplate.value.alias,
    projectCode: previewTemplate.value.project_code,
    projectName: previewTemplate.value.project_name
  }, dateRange[0], dateRange[1])
}

async function runExportJob(config, meta, startDate, endDate) {
  exporting.value = true
  exportProgress.visible = true
  let recordId = null
  try {
    const project = await resolveProjectForExport({
      project_code: meta.projectCode,
      project_name: meta.projectName
    })
    exportProgressCtrl = createExportProgressController(exportProgress)
    exportProgressCtrl.start(config.exportSheets || [], '正在读取模板配置...')
    exportProgressCtrl.report('正在创建导出记录...', { label: meta.templateAlias })

    recordId = await createExportRecord({
      templateId: meta.templateId,
      templateAlias: meta.templateAlias,
      projectCode: project.code,
      projectName: project.name,
      dateStart: startDate,
      dateEnd: endDate,
      configJson: config
    })
    await markRecordRunning(recordId)
    emit('export-started', recordId)

    const sheets = await runExportFromConfig(config, {
      project,
      startDate,
      endDate,
      ctrl: exportProgressCtrl
    })

    exportProgressCtrl.report('正在保存导出结果...', { label: '写入记录' })
    await markRecordDone(recordId, sheets.map((s) => ({
      name: s.name,
      rows: s.rows,
      merges: s.merges,
      colWidths: s.colWidths,
      rowHeights: s.rowHeights,
      themeKey: config.exportForm?.themeKey || 'ocean'
    })))

    exportProgressCtrl.report('正在生成 Excel...', { label: '下载' })
    exportWorkbook({
      sheets,
      fileName: buildExportFileName({
        projectName: project.name,
        projectCode: project.code,
        dataLabel: meta.templateAlias,
        startDate,
        endDate
      })
    })
    exportProgressCtrl.finish('success', '导出完成')
    ElMessage.success('导出成功')
    await load()
  } catch (err) {
    exportProgressCtrl?.finish('exception', '导出失败：' + (err.message || ''))
    if (recordId) await markRecordFailed(recordId, err.message || String(err)).catch(() => {})
    ElMessage.error('导出失败：' + (err.message || ''))
  } finally {
    exporting.value = false
  }
}

function statusLabel(s) {
  return { pending: '等待中', running: '进行中', done: '已完成', failed: '失败' }[s] || s
}

function statusTagType(s) {
  return { pending: 'info', running: 'warning', done: 'success', failed: 'danger' }[s] || 'info'
}

async function handleDownload(row) {
  downloadingId.value = row.id
  downloadProgressVisible.value = true
  downloadProgressPct.value = 0
  downloadProgressStatus.value = ''
  downloadProgressMsg.value = '正在读取导出数据...'
  try {
    downloadProgressPct.value = 30
    const record = await fetchRecordWithData(row.id)
    if (!record?.result_data) throw new Error('记录中无结果数据')
    downloadProgressPct.value = 60
    const resultData = typeof record.result_data === 'string'
      ? JSON.parse(record.result_data)
      : record.result_data
    const sheets = (Array.isArray(resultData) ? resultData : (resultData.sheets || []))
      .map((s) => ({ ...s, theme: getThemeConfig(s.themeKey || 'ocean') }))
    if (!sheets.length) throw new Error('暂无可导出的 Sheet 数据')
    downloadProgressPct.value = 90
    exportWorkbook({
      sheets,
      fileName: buildExportFileName({
        projectName: record.project_name,
        projectCode: record.project_code,
        dataLabel: record.template_alias || '导出记录',
        startDate: record.date_start,
        endDate: record.date_end
      })
    })
    downloadProgressPct.value = 100
    downloadProgressStatus.value = 'success'
    downloadProgressMsg.value = 'Excel 已生成'
  } catch (err) {
    downloadProgressStatus.value = 'exception'
    downloadProgressMsg.value = '下载失败：' + (err.message || '')
    ElMessage.error(downloadProgressMsg.value)
  } finally {
    downloadingId.value = null
  }
}

async function handleRerun(row) {
  if (rerunningId.value) return
  rerunningId.value = row.id
  rerunProgressVisible.value = true
  rerunProgressCtrl = createExportProgressController(rerunProgress)
  rerunProgress.pct = 0
  rerunProgress.status = ''
  try {
    const detail = await fetchRecordWithData(row.id)
    if (!detail?.config_json) throw new Error('记录中无配置数据')
    const config = typeof detail.config_json === 'string'
      ? JSON.parse(detail.config_json)
      : detail.config_json
    const project = await resolveProjectForExport({
      project_code: detail.project_code,
      project_name: detail.project_name
    })
    rerunProgressCtrl.start(config.exportSheets || [], '正在重新执行...')
    await markRecordRunning(row.id)
    const sheets = await runExportFromConfig(config, {
      project,
      startDate: detail.date_start,
      endDate: detail.date_end,
      ctrl: rerunProgressCtrl
    })
    await markRecordDone(row.id, sheets.map((s) => ({
      name: s.name, rows: s.rows, merges: s.merges,
      colWidths: s.colWidths, rowHeights: s.rowHeights,
      themeKey: config.exportForm?.themeKey || 'ocean'
    })))
    exportWorkbook({
      sheets,
      fileName: buildExportFileName({
        projectName: detail.project_name,
        projectCode: detail.project_code,
        dataLabel: detail.template_alias || '重跑导出',
        startDate: detail.date_start,
        endDate: detail.date_end
      })
    })
    rerunProgressCtrl.finish('success', '重新执行成功')
    ElMessage.success('重新执行成功')
    await load()
  } catch (err) {
    rerunProgressCtrl?.finish('exception', '重跑失败：' + (err.message || ''))
    await markRecordFailed(row.id, err.message || String(err)).catch(() => {})
    ElMessage.error('重跑失败：' + (err.message || ''))
  } finally {
    rerunningId.value = null
  }
}

defineExpose({ refresh })
</script>

<style scoped>
.template-center {
  display: flex;
  gap: 16px;
  height: 100%;
  min-height: 0;
}

.template-center.is-embedded {
  min-height: 62vh;
}

.template-sidebar {
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-right: 1px solid #e2e8f0;
  padding-right: 14px;
  min-height: 0;
}

.template-sidebar-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.template-sidebar-head h4,
.record-main-head h4 {
  margin: 0;
  font-size: 15px;
  color: #0f172a;
}

.template-sidebar-head p,
.record-main-head p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

.template-search {
  width: 100%;
}

.template-admin-tag {
  align-self: flex-start;
}

.template-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
}

.template-item {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #fff;
  padding: 10px 12px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.template-item:hover {
  border-color: #93c5fd;
}

.template-item.is-active {
  border-color: #3b82f6;
  box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.15);
  background: #eff6ff;
}

.template-item--generic {
  border-style: dashed;
  background: #f8fafc;
}

.template-item--generic.is-active {
  border-style: solid;
  background: #eff6ff;
}

.template-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.template-item-head strong {
  font-size: 13px;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-item-meta {
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.template-item-actions {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: stretch;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #eef2f7;
  max-width: 100%;
}

.template-item-actions.is-single {
  grid-template-columns: 1fr;
}

.tpl-act {
  width: 100%;
  min-width: 0;
  padding: 6px 4px;
  margin: 0;
  border: none;
  background: transparent;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
  white-space: nowrap;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
}

.template-item-actions:not(.is-single) .tpl-act:not(:last-child) {
  border-right: 1px solid #eef2f7;
}

.tpl-act--primary {
  color: #2563eb;
}

.tpl-act--primary:hover:not(:disabled) {
  color: #1d4ed8;
  background: #f8fafc;
}

.tpl-act--danger {
  color: #dc2626;
}

.tpl-act--danger:hover {
  color: #b91c1c;
  background: #fef2f2;
}

.tpl-act:disabled {
  cursor: wait;
  opacity: 0.65;
}

.tpl-act.is-loading {
  cursor: wait;
}

.record-project-code-label {
  display: block;
  font-size: 13px;
  color: #0f172a;
  font-weight: 500;
}

.record-project-name {
  display: block;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.record-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.record-main-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.record-toolbar {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.record-table-wrap {
  flex: 1;
  min-height: 0;
}

.record-pagination {
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.export-progress-panel {
  flex-shrink: 0;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #dbeafe;
  background: #f8fbff;
}

.export-progress-msg {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 8px;
}

.export-progress-current {
  margin-top: 8px;
  font-size: 12px;
  color: #475569;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.error-pre {
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  color: #be123c;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
}

.dl-progress-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.dl-progress-msg {
  font-size: 14px;
  color: #0f172a;
  font-weight: 600;
}

:deep(.record-actions-col .cell) {
  overflow: visible;
}

.record-action-buttons {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
  white-space: nowrap;
}

.rec-act {
  border: none;
  background: transparent;
  padding: 0 4px;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
  color: #64748b;
}

.rec-act:hover {
  color: #2563eb;
}

.rec-act--primary {
  color: #2563eb;
}

.rec-act--warn {
  color: #d97706;
}

.rec-act--warn:hover {
  color: #b45309;
}

.rec-act--danger {
  color: #dc2626;
}

.rec-act--danger:hover {
  color: #b91c1c;
}

.rec-act:disabled {
  cursor: wait;
  opacity: 0.6;
}
</style>
